import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, EventEmitter, OnDestroy, OnInit, Output, Renderer2 } from '@angular/core';
import { ScaledImage } from '@cf-shared/data-model/scaled-image';
import { ImageService } from 'services/image-service';
import { DestroyGuard } from 'services/browser-utils';
import { HTMLInputEvent } from 'data-model/html-input';
import { AlertController } from 'providers/alert-controller';
import { Loading, LoadingController } from 'providers/loading-controller';
import { TranslatedString } from 'models/translated-string';

import { LL } from 'i18n/i18n.service';

// live EDITABLE uploader instances, in mount order. pages can render several
// image-uploader instances at once (edit-food shows up to four, two of them
// read-only), so the window.__e2ePickImage E2E hook is a dispatcher over this
// registry: it targets the newest live editable instance, stays installed as
// long as any editable instance remains mounted, and disappears with the last
// one — no instance can delete it out from under a live sibling.
const liveEditableUploaders: ImageUploaderComponent[] = [];

/**
 * Based on:
 * @tutorial https://devdactic.com/ionic-2-images/
 *
 * - Also added support for actionlist usage in the browser
 */
@Component({
    selector: 'image-uploader',
    templateUrl: 'image-uploader.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImageUploaderComponent implements OnInit, OnDestroy {
  protected LL = LL;
  private loader: Loading;
  private destroyGuard = new DestroyGuard();
  // the in-flight file-pick input. it must stay DOM-attached AND referenced
  // here until its change event fires — see addImage for the WKWebView
  // garbage-collection trap this guards against.
  private pendingFileInput: HTMLInputElement | null = null;

  @Input() placeholderImagePath: string = 'assets/imgs/photo_placeholder.svg';
  @Input() image: ScaledImage;
  @Input() imageId: string = '';
  @Input() isReadOnly: boolean;
  @Input() offerRemoveOption: boolean = false;
  @Input() createMultipleSizes: boolean = true;
  // optional override for the minimum accepted image size (shorter edge, px).
  // when unset, ImageService applies its size-mode default. lets the coach
  // logo uploader accept small logos.
  @Input() minImageSize?: number;
  @Output() updateImage: EventEmitter<ScaledImage> = new EventEmitter();
  // the image whose photo could not load. held here, not on the image, because
  // the image is the food's own object: clearing its sizes used to delete the
  // photo from the food on the next save (#5692). a new image is not affected.
  private failedImage: ScaledImage | null = null;

  constructor(
    private loadingCtrl: LoadingController,
    protected imageService: ImageService,
    private renderer: Renderer2,
    private alertCtrl: AlertController,
    private cdRef: ChangeDetectorRef,
  ) {}

  hasImage(): boolean {
    return this.image && this.image.sizes && this.image.sizes.length > 0 && this.image !== this.failedImage;
  }

  ngOnInit() {
    // if this instance is read-only it can never accept a picked image (no
    // updateImage consumer), so it does not join the E2E hook registry
    if (this.isReadOnly) {
      return;
    }
    // expose a hook so E2E tests can inject a picked image without the
    // browser's file dialog (same lifecycle-scoped pattern as
    // db-restore-json's __e2eLoadFixtureJson). the hook dispatches to the
    // NEWEST live editable instance (see liveEditableUploaders).
    liveEditableUploaders.push(this);
    (window as unknown as Record<string, unknown>).__e2ePickImage = (width: number, height: number) => {
      const target = liveEditableUploaders[liveEditableUploaders.length - 1];
      // test-only scaffolding: fail loudly so the E2E run reports a clear cause
      if (!target) {
        throw new Error('__e2ePickImage: no editable image-uploader is mounted');
      }
      target.e2ePickImage(width, height);
    };
  }

  ngOnDestroy() {
    // leave the e2e registry; the hook disappears with the last editable
    // instance and keeps dispatching to survivors until then
    const registryIndex = liveEditableUploaders.indexOf(this);
    if (registryIndex >= 0) {
      liveEditableUploaders.splice(registryIndex, 1);
    }
    if (liveEditableUploaders.length === 0) {
      delete (window as unknown as Record<string, unknown>).__e2ePickImage;
    }
    this.removePendingFileInput();
    this.destroyGuard.destroy();
  }

  /** E2E-only entry point behind window.__e2ePickImage: draws a canvas (no
   * binary fixture file needed) and runs the generated File through the EXACT
   * same processing path as a real pick (resize -> upload -> offline enqueue). */
  e2ePickImage(width: number, height: number) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    // test-only scaffolding: a null context/blob means the environment
    // cannot draw — fail loudly so the E2E run reports a clear cause
    if (!ctx) {
      throw new Error('__e2ePickImage: 2d canvas context unavailable');
    }
    ctx.fillStyle = '#37a26e';
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(width / 4, height / 4, width / 2, height / 2);
    canvas.toBlob(blob => {
      if (!blob) {
        throw new Error('__e2ePickImage: canvas.toBlob produced no blob');
      }
      const file = new File([blob], 'e2e-photo.jpg', { type: 'image/jpeg' });
      void this.onImageUploadAsync({ target: { files: [file] } } as unknown as HTMLInputEvent);
    }, 'image/jpeg', 0.9);
  }

  /** Keeps a queued (offline outbox) photo visible when its URL cannot load;
   * any other photo that cannot load is only hidden on screen. The food keeps
   * its image reference, because offline every photo fails to load (#5692). */
  handleImageLoadError(event?: Event) {
    void (async () => {
      // if the failed src is a blob: preview, the drain just revoked it after
      // a successful upload — the next render swaps in the now-valid remote
      // url, so the reference must stay
      const failedSrc = (event?.target as HTMLImageElement | undefined)?.currentSrc ?? '';
      if (failedSrc.startsWith('blob:')) {
        this.cdRef.markForCheck();
        return;
      }
      // #5692: a file of the native image cache that has gone missing. the
      // cache forgets it, and the next render falls back to the remote url
      if (this.imageService.forgetLocalImageCopy(failedSrc)) {
        this.cdRef.markForCheck();
        return;
      }
      // #3989 follow-up: while this photo's bytes are still queued in the
      // offline upload outbox, its public URL cannot load — that is not a
      // broken image. keep the sizes; the re-render substitutes the outbox's
      // local preview URL (a different src), which makes the browser retry.
      // hiding it here would wipe the just-taken photo from the form.
      const queued = await this.isAnySizeQueuedAsync();
      if (this.destroyGuard.isDestroyed) {
        return;
      }
      if (!queued) {
        this.failedImage = this.image;
      }
      this.cdRef.markForCheck();
    })();
  }

  private async isAnySizeQueuedAsync(): Promise<boolean> {
    // all size variants of one photo enqueue together, so any queued size
    // means the photo is local. legacy sizes (no path) never match.
    for (const size of this.image?.sizes ?? []) {
      if (size.url && await this.imageService.isQueuedImageUrlAsync(size.url)) {
        return true;
      }
    }
    return false;
  }

  handleRemoveImage() {
    void (async () => {
      await this.removeImage();

      this.image = null;
      this.updateImage.emit(this.image);
      this.cdRef.markForCheck();
    })();
  }

  async removeImage() {
    // best-effort: queued variants are cancelled locally, uploaded variants
    // get a server delete that may fail offline (handled inside the
    // service). this never rejects, so the CALLERS (handleRemoveImage
    // clearing the image, onImageUploadAsync starting the replacement)
    // always proceed — before #3989 follow-up 2 an offline Remove rejected
    // here and the button silently did nothing.
    if (this.image && this.image.sizes) {
      await this.imageService.deleteImageSizesAsync(this.image.sizes);
    }
  }

  addImage() {
    // the input must be BOTH attached to the DOM and referenced from the
    // component until the pick resolves: WKWebView garbage-collects a
    // detached, unreferenced file input while the OS picker is open, and the
    // user's selection is then silently discarded (observed twice during the
    // #3989 on-device verification — "nothing happened" on a library pick,
    // identical retry worked). scanner-file-picker.ts is the repo precedent
    // for this attach + cleanup lifetime pattern.
    // deliberately NO timed cancel-sweep (unlike scanner-file-picker): on iOS
    // the change event can arrive SECONDS after the picker closes (HEIC
    // export), and de-rooting the input on a timer would reintroduce the
    // lost-pick bug. a cancelled pick just leaves ONE hidden input parked
    // until the next pick or the component's destroy.
    this.removePendingFileInput();
    const inputElement = this.renderer.createElement('input') as HTMLInputElement;
    this.renderer.setAttribute(inputElement, 'style', 'display:none');
    this.renderer.setAttribute(inputElement, 'type', 'file');
    this.renderer.setAttribute(inputElement, 'accept', 'image/png, image/jpeg, image/heic, image/jpg');
    this.renderer.listen(inputElement, 'change', event => {
      this.removePendingFileInput();
      void this.onImageUploadAsync(event);
    });
    document.body.appendChild(inputElement);
    this.pendingFileInput = inputElement;
    inputElement.click();
  }

  private removePendingFileInput() {
    if (this.pendingFileInput) {
      this.pendingFileInput.remove();
      this.pendingFileInput = null;
    }
  }

  async onImageUploadAsync(event: HTMLInputEvent) {
    this.loader = this.loadingCtrl.create();
    void this.loader.present();
    let target = event.target;
    await this.removeImage();
    // TODO: This is not actualy properly async!!!
    this.imageService.processUploadImages(
      target.files,
      this.imageId,
      img => this.setImage(img),
      message => this.failedUpload(message),
      this.createMultipleSizes,
      this.minImageSize,
    );
  }

  setImage(image: ScaledImage) {
    void this.loader.dismiss();
    this.updateImage.emit(image);
    this.cdRef.markForCheck();
  }

  async failedUpload(message?: TranslatedString) {
    void this.loader.dismiss();
    if (message) {
      await this.alertCtrl.showAlertAsync({ message });
    }
    this.cdRef.markForCheck();
  }
}
